// src/features/health/pages/MedicationDetailPage.tsx
// BP-03-04 Part 3: Individual medication detail page

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Pill } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/AlertDialog';
import { PageHeader } from '@/components/layout/PageHeader';
import { MedicationDetails } from '../components/MedicationDetails';
import { MedicationForm } from '../components/MedicationForm';
import { useMedication, useDeleteMedication } from '../hooks/useMedications';

export const MedicationDetailPage: React.FC = () => {
  const { childId, medicationId } = useParams<{ childId: string; medicationId: string }>();
  const navigate = useNavigate();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const { data: medication, isLoading, error } = useMedication(childId!, medicationId!);
  const deleteMutation = useDeleteMedication(childId!);

  if (!childId || !medicationId) {
    return <div>Missing required parameters</div>;
  }

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(medicationId);
      navigate(`/children/${childId}/medications`);
    } catch (error) {
      console.error('Failed to delete medication:', error);
    }
  };

  const handleEditSuccess = () => {
    setShowEditDialog(false);
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl">
      <PageHeader
        title={medication?.name || 'Medication'}
        description={medication?.dosage}
        icon={<Pill className="w-6 h-6 text-blue-500" />}
        actions={
          <div className="flex items-center gap-2">
            <Button 
              variant="outline"
              onClick={() => setShowEditDialog(true)}
              disabled={isLoading}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button 
              variant="outline"
              onClick={() => setShowDeleteDialog(true)}
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
              disabled={isLoading}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        }
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Child', href: `/children/${childId}` },
          { label: 'Medications', href: `/children/${childId}/medications` },
          { label: medication?.name || 'Loading...' }
        ]}
        backHref={`/children/${childId}/medications`}
      />

      <div className="mt-6">
        <MedicationDetails
          childId={childId}
          medicationId={medicationId}
          onEdit={() => setShowEditDialog(true)}
          onDelete={() => setShowDeleteDialog(true)}
        />
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Edit Medication</DialogTitle>
          </DialogHeader>
          {medication && (
            <MedicationForm 
              childId={childId}
              medication={medication}
              onSuccess={handleEditSuccess}
              onCancel={() => setShowEditDialog(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Medication</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {medication?.name}? This will also delete all 
              associated dose logs and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MedicationDetailPage;


